import _ from 'lodash';

import { bindServices } from '@/services/api';
import { toPaginate } from '@/utils/infrastructure';

const api = bindServices('processConfigForm');
const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const Model = {
  namespace: 'processConfigForm',
  state: {
    newForm: false,
    selectedItem: null,
    formConfig: null,
    list: [],
    listUserPermission: null,
    listPositionPermission: null,
    listUserGroupPermission: null,
    currentFormData: null,
    pagination: meta,
    paramsView: {
      select: 'id,isActive,name,description,internalName,isInitialForm,created,updated',
      expand: {
        userCreator: { select: 'fullName' },
        userModifier: { select: 'fullName' },
        process: { select: 'name' },
      },
    },
    paramsEdit: {
      select: 'id,isActive,name,description,internalName,isInitialForm',
    },
    view: false,
    orderBy: '',
    id: null,
    loadingItem: false,
    activeFormId: null,
    pendingIdUpdate: null,
  },

  effects: {
    *fetchList({ payload }, { call, put }) {
      try {
        const { processId, versionId, token, ...rest } = payload;
        const res = yield call(api.apiRoute, {
          route: `process/config/${processId}/${versionId}/forms`,
          select: 'id,formId,name,description,isActive,configurations,processId',
          params: { token },
          ...rest,
        });

        yield put({ type: 'queryForms', payload: res });
      } catch (e) {}
    },

    *fetchForm({ payload }, { call, put }) {
      try {
        const { activeFormId, versionId, token, processId } = payload;
        const res = yield call(api.apiRoute, {
          route: `process/config/${processId}/${versionId}/forms/${activeFormId}`,
          params: { token },
        });

        yield put({ type: 'queryFormData', payload: res });
      } catch (e) {}
    },

    *saveFormConfig({ payload }, { call, put }) {
      yield put({ type: 'global/clearValidationForm' });
      try {
        const { versionId, token, processId, data, elementId } = payload;
        const action = data?.itemId ? 'modify' : 'append';

        const res = yield call(api.apiRoute, {
          route: `process/config/${action}/${processId}/${versionId}`,
          params: { token },
          method: 'POST',
          data,
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        if (res?.newItemId && elementId) {
          yield put({
            type: 'resolveElementId',
            payload: { draftId: elementId, newItemId: res.newItemId },
          });
        }

        return true;
      } catch (e) {
        if (e?.validationErrors) {
          yield put({
            type: 'global/validationErrors',
            payload: e.validationErrors,
          });
        }
      }
    },

    *addNewForm({ payload }, { put }) {
      yield put({ type: 'addForm', payload });
    },

    *editItemForm({ payload }, { call, put }) {
      try {
        const table = yield call(api.getById, {
          route: 'form',
          id: payload.id,
          params: payload.params,
        });

        yield put({
          type: 'editForm',
          payload: { ...payload, state: true, item: table },
        });
      } catch (e) {}
    },

    *saveNewForm({ payload }, { call, put }) {
      try {
        const { id, versionId, token, data } = payload;
        const res = yield call(api.create, {
          route: `process/config/append/${id}/${versionId}`,
          params: { token },
          data,
        });

        yield put({
          type: 'global/showNotificationsForm',
          payload: { visible: true, type: 'success' },
        });

        if (res?.newItemId) {
          yield put({ type: 'setActiveForm', payload: res.newItemId });
        }
        yield put({ type: 'hideForms' });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: e.message,
              validationErrors: e.validationErrors,
            },
          });
        }
      }
    },

    *saveEditForm({ payload }, { call, put }) {
      try {
        const { id, versionId, token, data } = payload;
        yield call(api.update, {
          route: `process/config/modify/${id}/${versionId}`,
          params: { token },
          method: 'post',
          data,
        });

        yield put({
          type: 'global/showNotificationsForm',
          payload: { visible: true, type: 'success' },
        });

        yield put({ type: 'queryCRUD' });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: e.message,
              validationErrors: e.validationErrors,
            },
          });
        }
      }
    },

    *removeCanvasElement({ payload }, { call, put }) {
      try {
        const { versionId, token, processId, data } = payload;
        yield call(api.apiRoute, {
          route: `process/config/remove/${processId}/${versionId}`,
          params: { token },
          method: 'POST',
          data,
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        return true;
      } catch (e) {
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'error' },
        });

        return false;
      }
    },

    *deleteItem({ payload }, { call, put }) {
      try {
        const { id, versionId, token, data } = payload;
        yield put({ type: `global/modalConfirm`, state: false, id: null });
        yield call(api.apiRoute, {
          route: `process/config/remove/${id}/${versionId}`,
          params: { token },
          method: 'post',
          data,
        });

        yield put({
          type: 'global/showFormsGlobal',
          payload: { visible: true, type: 'success' },
        });

        yield put({ type: 'queryCRUD' });
      } catch (e) {
        if (e?.message) {
          yield put({ type: `global/modalConfirm`, state: false, id: null });
          yield put({
            type: `global/showForms${payload.view ? 'Form' : 'Global'}`,
            payload: {
              visible: true,
              type: 'error',
              message: e.message,
              autoHide: payload.view,
            },
          });
        }
      }
    },

    *showItemForm({ view }, { put }) {
      yield put({ type: 'showEditItemForm', view });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'orderBy', payload });
    },

    *editActive({ payload, params }, { call, put }) {
      try {
        yield call(api.apiRoute, { route: `form/activate/${params}`, method: 'post' });
        yield put({ type: 'editActives', payload });
      } catch (e) {}
    },

    *fetchListPermissions({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, { route: payload.route, params: payload.params });
        yield put({
          type: 'queryPermission',
          payload: {
            data: res,
            name: payload.name,
            field: payload.field,
          },
        });
      } catch (e) {}
    },

    *addNewPermission({ payload }, { put }) {
      yield put({ type: 'addPermission', payload });
    },

    *addListPermission({ payload }, { put }) {
      yield put({ type: 'addListPermissions', payload });
    },

    *deleteItemPermision({ payload }, { put }) {
      yield put({ type: 'deleteItemPermisions', payload });
      yield put({ type: `global/modalConfirm`, state: false, id: null });
    },

    *savePermission({ payload }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `form/updatePermissions/${payload.id}`,
          method: 'post',
          data: payload.data,
        });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        yield put({
          type: 'addPermission',
          payload: { state: false, item: null },
        });

        yield put({ type: 'hidePermission' });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: false,
              visibleForm: true,
              type: 'error',
              message: e.message,
            },
          });
        }
      }
    },

    *hideAllForms(_, { put }) {
      yield put({ type: 'hideForms' });
    },

    *closeBladeOrForm({ payload }, { put, select }) {
      const { newForm } = yield select((state) => state.processConfigForm);
      if (newForm) yield put({ type: 'hideForms' });
      else yield put({ type: 'processConfig/closeBladesFromIndex', payload });
    },

    *clearActiveFormId(_, { put }) {
      yield put({ type: 'clearActiveForm' });
    },
  },

  reducers: {
    queryForms(state, { payload }) {
      return {
        ...state,
        list: payload.items,
        pagination: { ...state.pagination, ..._.omitBy(toPaginate(payload), _.isUndefined) },
        crudOperation: false,
      };
    },

    queryFormData(state, { payload }) {
      return { ...state, currentFormData: payload };
    },

    updateFormData(state, { payload }) {
      if (!state.currentFormData) return state;
      return { ...state, currentFormData: payload };
    },

    addForm(state, { payload }) {
      return {
        ...state,
        newForm: payload.state,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
      };
    },

    editOrViewForm(state) {
      return { ...state, selectedItem: {} };
    },

    editForm(state, { payload }) {
      return {
        ...state,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
        newPermission: false,
      };
    },

    saveForm(state, { payload }) {
      return {
        ...state,
        saveForm: payload,
        newForm: false,
        selectedItem: null,
      };
    },

    queryCRUD(state) {
      return { ...state, crudOperation: true };
    },

    setActiveForm(state, { payload }) {
      return { ...state, activeFormId: payload || null, currentFormData: null };
    },

    clearActiveForm(state) {
      return { ...state, activeFormId: null };
    },

    showEditItemForm(state, { view }) {
      return { ...state, view };
    },

    addPermission(state, { payload }) {
      return {
        ...state,
        newPermission: payload.state,
        newForm: false,
        selectedItem: payload.item,
      };
    },

    queryPermission(state, { payload }) {
      return {
        ...state,
        [payload.name]: _.map(payload.data, (l) => {
          return {
            ...l[payload.field],
            id: l[`${payload.field}Id`],
          };
        }),
      };
    },

    addListPermissions(state, { payload }) {
      return {
        ...state,
        [payload.name]: [...(state[payload.name] || []), payload.data],
      };
    },

    deleteItemPermisions(state, { payload }) {
      return {
        ...state,
        [payload.name]: _.filter(state[payload.name], (l) => l.id !== payload.id),
      };
    },

    editActives(state, { payload }) {
      const list = state.list.map((item) => (item.id === payload.id ? payload : item));
      return { ...state, list };
    },

    hidePermission(state) {
      return {
        ...state,
        selectedItem: null,
        listUserPermission: null,
        listPositionPermission: null,
        listUserGroupPermission: null,
        formConfig: null,
      };
    },

    orderBy(state, { payload }) {
      return { ...state, orderBy: payload };
    },

    hideForms(state) {
      return {
        ...state,
        newForm: false,
        selectedItem: null,
        view: null,
        listUserPermission: null,
        listPositionPermission: null,
        listUserGroupPermission: null,
        orderBy: '',
        id: null,
      };
    },

    resolveElementId(state, { payload }) {
      return { ...state, pendingIdUpdate: payload };
    },

    clearPendingIdUpdate(state) {
      return { ...state, pendingIdUpdate: null };
    },
  },
};

export default Model;
